import { lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import Seo from "@/components/Seo";
import { scrollToAnchor } from "@/lib/scrollToAnchor";

// Above-the-fold: load eagerly (Navbar + Hero)
// Below-the-fold: lazy-load to reduce initial JS payload
const PhilosophySection = lazy(() => import("@/components/PhilosophySection"));
const PolesSection = lazy(() => import("@/components/PolesSection"));
const UniversesSection = lazy(() => import("@/components/UniversesSection"));
const CollectionSection = lazy(() => import("@/components/CollectionSection"));
const ProcessSection = lazy(() => import("@/components/ProcessSection"));
const WhySection = lazy(() => import("@/components/WhySection"));
const ProjectsSection = lazy(() => import("@/components/ProjectsSection"));
const MapSection = lazy(() => import("@/components/MapSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const Footer = lazy(() => import("@/components/Footer"));

const Index = () => {
  const { hash } = useLocation();

  // When arriving with a hash (e.g. /#contact from the catalogue),
  // wait for the lazy-loaded target section to mount before scrolling.
  useEffect(() => {
    if (!hash) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 60; // ~3s at 50ms intervals
    let settledAttempts = 0;
    const maxSettledAttempts = 24; // keep realigning while lazy content/fonts settle

    const tryScroll = () => {
      if (cancelled) return;
      if (scrollToAnchor(hash)) {
        if (settledAttempts++ < maxSettledAttempts) {
          window.setTimeout(tryScroll, 50);
        }
        return;
      }
      if (attempts++ < maxAttempts) {
        window.setTimeout(tryScroll, 50);
      }
    };

    tryScroll();
    return () => {
      cancelled = true;
    };
  }, [hash]);

  return (
    <main>
      <Seo
        title="Scénographie végétale et location de plantes | Visionature"
        description="Visionature conçoit des scénographies végétales, loue des plantes et réalise des aménagements sur mesure pour événements, hôtels, restaurants et entreprises."
        canonicalPath="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Visionature",
          url: "https://visionature.net/",
        }}
      />
      <Navbar />
      <HeroSection />
      <Suspense fallback={null}>
        <PhilosophySection />
        <PolesSection />
        {/* <UniversesSection /> — hidden, kept for future reactivation */}
        <CollectionSection />
        <ProcessSection />
        <WhySection />
        <ProjectsSection />
        <MapSection />
        <ContactSection />
        <Footer />
      </Suspense>
    </main>
  );
};

export default Index;
